import { warn } from '@execonline-inc/logging';
import { assertNever } from '@kofno/piper';
import { nothing } from 'maybeasy';
import { Task } from 'taskarian';
import { callApi } from '../../../../../Appy';
import { GroupResource } from '../../../../../GroupsStore/Types';
import { findLinkT } from '../../../../../LinkyLinky';
import { MulticheckStore } from '../../../../../Multicheck/Store';
import ReactionComponent, { RCProps } from '../../../../../ReactionComponent';
import {
  FormOutValues,
  SelectionOption,
  fieldsToTemplate,
  fieldsToValues,
  findFieldT,
} from '../../../../../Resource/FormTemplates/Types';
import { Link } from '../../../../../Resource/Types';
import { fromNullableT } from '../../../../../TaskExt';
import { newBulkLicenseAllocationsResourceDecoder } from '../../../Participants/BulkActions/AllocateLicenseToParticipants/Decoders';
import {
  NewBulkLicenseAllocationsResource,
  invalidFormValues,
  missingTemplate,
} from '../../../Participants/BulkActions/AllocateLicenseToParticipants/Types';
import { licenseAllocationsFormResourceDecoder } from '../../../Participants/Table/LicenseAllocations/Decoders';
import { LicenseAllocationsFormResource } from '../../../Participants/Table/LicenseAllocations/Types';
import { submissionResponseDecoder } from './Decoders';
import { AddLicenseToGroupsStore } from './Store';
import { LoadError, State, SubmissionResponse, SubmitError } from './Types';

const getForm = callApi(newBulkLicenseAllocationsResourceDecoder, {});

const submitLicenseAllocations = (values: FormOutValues) =>
  callApi(submissionResponseDecoder, values);

const handleSubmitResponse = (store: AddLicenseToGroupsStore) => (response: SubmissionResponse) => {
  switch (response.kind) {
    case 'submission-success':
      store.submitted(response.resource);
      break;
    case 'validation-errors':
      store.ready(response.resource);
      break;
    default:
      assertNever(response);
  }
};

const idToOption = (id: number): SelectionOption => ({
  value: id.toString(),
  label: '',
  image: nothing(),
  link: nothing(),
});

const selectedUserIdOptions = (
  multicheckStore: MulticheckStore<GroupResource>,
): Array<SelectionOption> => {
  return multicheckStore.selected
    .flatMap((group) => group.payload.participants.getOrElseValue([]))
    .map(({ id }) => id)
    .map(idToOption);
};

const submitNewForm =
  (multicheckStore: MulticheckStore<GroupResource>) =>
  (resource: NewBulkLicenseAllocationsResource): Task<LoadError, LicenseAllocationsFormResource> =>
    Task.succeed<LoadError, {}>({})
      .assign(
        'template',
        fromNullableT(resource.templates.self).mapError(() => missingTemplate('self')),
      )
      .assign('link', ({ template }) => findLinkT(template.target, resource.links))
      .assign('field', ({ template }) =>
        findFieldT('user_ids', 'multiple-selection', template.fields),
      )
      .do(({ field }) => {
        field.value = selectedUserIdOptions(multicheckStore);
      })
      .andThen(({ field, template, link }) =>
        fieldsToValues([field])
          .mapError(fieldsToTemplate(template))
          .mapError(({ fields }) =>
            invalidFormValues(
              fields.flatMap(({ errors }) => errors),
              fields.filter((f) => f.errors.length > 0),
            ),
          )
          .cata<Task<LoadError, LicenseAllocationsFormResource>>({
            Ok: (values) => callApi(licenseAllocationsFormResourceDecoder, values)(link),
            Err: Task.fail,
          }),
      );

class Reactions extends ReactionComponent<
  AddLicenseToGroupsStore,
  State,
  RCProps<AddLicenseToGroupsStore>
> {
  tester = () => this.props.store.state;
  effect = (state: State) => {
    const { store } = this.props;
    switch (state.kind) {
      case 'waiting':
        break;
      case 'loading':
        Task.succeed<LoadError, Link>(state.link)
          .andThen(getForm)
          .andThen(submitNewForm(state.multicheckStore))
          .fork(store.loadingError, store.ready);
        break;
      case 'loading-error':
        warn('Could not load license allocations form', JSON.stringify(state.error));
        break;
      case 'ready':
        break;
      case 'submitting':
        Task.succeed<SubmitError, ReadonlyArray<Link>>(state.resource.links)
          .andThen(findLinkT(state.template.target))
          .andThen(submitLicenseAllocations(state.values))
          .fork(store.submittingError, handleSubmitResponse(store));
        break;
      case 'submitting-error':
        warn('Could not submit license allocations form', JSON.stringify(state.error));
        break;
      case 'submitted':
        break;
      default:
        assertNever(state);
    }
  };
}

export default Reactions;
